<script setup lang="ts">
import { computed, ref } from 'vue'
import { LoginStage, useAuthStore } from '@/stores/auth'
import InputField from '@/components/InputField.vue'
import BasicButton from '@/components/BasicButton.vue'
import SpinnerBasic from '@/components/SpinnerBasic.vue'
import FormError from '@/components/FormError.vue'
import { useI18n } from 'vue-i18n'
import { ArrowGotoIcon } from '@/assets'
import ResetPassword from './ResetPassword.vue'
import LinkButton from '@/components/LinkButton.vue'

const authStore = useAuthStore()
const { t } = useI18n()

const email = ref('')
const password = ref('')
const confirmationCode = ref('')

const isLoading = ref(false)
const isError = ref(false)
const errorMessage = ref<() => string>(() => '')
const isPasswordResetView = ref(false)
//TODO - introduce password was reset stage

async function handleLogin() {
  isError.value = false
  isLoading.value = true
  const status = await authStore.login({ username: email.value, password: password.value })

  switch (status) {
    case 'loggedIn':
      isError.value = false
      break
    case 'nextActionRequired':
      isError.value = false
      break
    case 'passwordResetRequired': //TODO - handle password reset flow
      errorMessage.value = () => t('authenticatorCard.error.passwordResetRequired')
      isError.value = true
      break
    case 'userNotAuthorizedError':
      errorMessage.value = () => t('authenticatorCard.error.userNotAuthorizedError')
      isError.value = true
      break
    case 'tooManyRequestsError':
      errorMessage.value = () => t('authenticatorCard.error.tooManyRequestsError')
      isError.value = true
      break
    default:
      errorMessage.value = () => t('authenticatorCard.error.unknownError')
      isError.value = true
  }

  //TODO - handle errors and show error message
  isLoading.value = false
}

async function handleConfirmationCode() {
  isError.value = false
  isLoading.value = true
  const status = await authStore.loginNextStep({ challengeResponse: confirmationCode.value })

  switch (status) {
    case 'loggedIn':
      isError.value = false
      break
    case 'nextActionRequired':
      isError.value = false
      break
    case 'passwordResetRequired': //TODO - handle password reset flow
      errorMessage.value = () => t('authenticatorCard.error.passwordResetRequired')
      isError.value = true
      break
    case 'userNotAuthorizedError':
      errorMessage.value = () => t('authenticatorCard.error.userNotAuthorizedError')
      isError.value = true
      break
    case 'tooManyRequestsError':
      errorMessage.value = () => t('authenticatorCard.error.tooManyRequestsError')
      isError.value = true
      break
    case 'codeMismatchError':
      errorMessage.value = () => t('authenticatorCard.error.codeMismatchError')
      isError.value = true
      break
    case 'expiredCodeError':
      errorMessage.value = () => t('authenticatorCard.error.expiredCodeError')
      isError.value = true
      break
    case 'MFAMethodNotFoundError':
      errorMessage.value = () => t('authenticatorCard.error.MFAMethodNotFoundError')
      isError.value = true
      break
    case 'enableSoftwareTokenMFAError':
      errorMessage.value = () => t('authenticatorCard.error.enableSoftwareTokenMFAError')
      isError.value = true
      break
    case 'softwareTokenMFANotFoundError':
      errorMessage.value = () => t('authenticatorCard.error.softwareTokenMFANotFoundError')
      isError.value = true
      break
    case 'concurrentModificationError':
      errorMessage.value = () => t('authenticatorCard.error.concurrentModificationError')
      isError.value = true
      break
    default:
      errorMessage.value = () => t('authenticatorCard.error.unknownError')
      isError.value = true
  }

  isLoading.value = false
}

async function cancelLogin() {
  isError.value = false
  isLoading.value = true
  await authStore.logout()
  isLoading.value = false
}

async function initiateResetPassword() {
  isPasswordResetView.value = true
}

async function closeResetPassword() {
  isPasswordResetView.value = false
}

const isNextActionInput = computed(() => authStore.loginStage === LoginStage.AwaitingNextAction)

//TODO - handle bad pasword/code/login failure
//TODO - handle TOTP and SMS
//TODO - description of each step
//TODO - i18n
//TODO - handle rate limiting error - Too many attempts, try again later...
//TODO - signing in animation
//TODO - what if I request SMS challenge but it's not allowed? handle exceptions and error messages

// const twoFactorCode = ref()
</script>
<template>
  <div class="authenticator-frame">
    <ResetPassword v-if="isPasswordResetView" :email="email" @close="closeResetPassword" />
    <template v-else>
      <h1>{{ $t('authenticatorCard.title') }}</h1>
      <form v-if="!isNextActionInput" class="authenticator-fields" @submit.prevent="handleLogin">
        <InputField v-model="email" type="email" autocomplete="email webauthn" required>{{ $t('common.email') }}:</InputField>
        <InputField v-model="password" type="password" autocomplete="curent-password webauthn" required>{{ $t('common.password') }}:</InputField>

        <LinkButton :disabled="isLoading" :icon="ArrowGotoIcon" @click="isPasswordResetView = true">{{
          $t('authenticatorCard.resetPassword')
        }}</LinkButton>
        <FormError v-if="isError">{{ errorMessage() }}</FormError>
        <BasicButton class="submit-button" :disabled="isLoading" type="submit">
          <span v-if="!isLoading">{{ $t('authenticatorCard.signIn') }}</span>
          <SpinnerBasic v-else size="sm" />
        </BasicButton>
      </form>

      <form
        v-else-if="authStore.nextAction?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' || 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'"
        class="authenticator-fields"
        @submit.prevent="handleConfirmationCode"
      >
        <h2 v-if="authStore.nextAction?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'">{{ $t('authenticatorCard.stage.enterEmailCode') }}</h2>
        <h2 v-else>{{ $t('authenticatorCard.stage.enterTotp') }}</h2>
        <InputField v-model="confirmationCode" type="text" autocomplete="one-time-code" required>{{
          $t('authenticatorCard.oneTimeCode')
        }}</InputField>

        <FormError v-if="isError">{{ errorMessage() }}</FormError>
        <BasicButton class="submit-button" :disabled="isLoading" type="submit">
          <span v-if="!isLoading">{{ $t('authenticatorCard.signIn') }}</span>
          <SpinnerBasic v-else size="sm" />
        </BasicButton>
      </form>
      <BasicButton v-if="isNextActionInput" :disabled="isLoading" @click="cancelLogin">
        <span v-if="!isLoading">{{ $t('authenticatorCard.cancel') }}</span>
        <SpinnerBasic v-else size="sm" />
      </BasicButton>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.authenticator-frame {
  max-width: 100%;
}

.authenticator-fields {
  margin-top: 40px;
  max-width: 100%;
  width: 400px;

  > * {
    margin-bottom: 16px;
  }
}

.submit-button {
  margin-top: 40px;
  display: block;
  width: 100%;
}

.description {
  margin-bottom: 40px;
}

.new-section {
  margin-top: 80px;
  margin-bottom: 16px;
}

.password-requirements {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 24px;
  margin-bottom: 16px;
}

.password-requirements-description {
  margin-bottom: 12px;
}
</style>
